import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Pencil, Search } from 'lucide-react';
import api from '../services/api';

interface AdminSellerRow {
  id: string;
  name: string;
  logo?: string;
  isPinned?: boolean;
  isPlatformStore?: boolean;
  promotionActive?: boolean;
  promotionStartsAt?: string;
  promotionExpiresAt?: string;
  promotionTier?: number;
  ownerEmail?: string;
}

const toDatetimeLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const BoutiquePromotions: React.FC = () => {
  const { t } = useTranslation('boutiques');
  const [sellers, setSellers] = useState<AdminSellerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editing, setEditing] = useState<AdminSellerRow | null>(null);
  const [form, setForm] = useState({
    promotionActive: false,
    promotionStartsAt: '',
    promotionExpiresAt: '',
    promotionTier: 1,
  });
  const [saving, setSaving] = useState(false);

  const fetchSellers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: '20',
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      const res = await api.get(`/admin/sellers?${params}`);
      const data = res.data?.data || res.data;
      setSellers(data.sellers || []);
      setTotalPages(data.totalPages || 1);
      setError('');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, search, t]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const openEdit = (row: AdminSellerRow) => {
    setEditing(row);
    setForm({
      promotionActive: row.promotionActive ?? false,
      promotionStartsAt: toDatetimeLocal(row.promotionStartsAt),
      promotionExpiresAt: toDatetimeLocal(row.promotionExpiresAt),
      promotionTier: row.promotionTier ?? 1,
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/sellers/${editing.id}/promotion`, {
        promotionActive: form.promotionActive,
        promotionStartsAt: form.promotionStartsAt
          ? new Date(form.promotionStartsAt).toISOString()
          : null,
        promotionExpiresAt: form.promotionExpiresAt
          ? new Date(form.promotionExpiresAt).toISOString()
          : null,
        promotionTier: form.promotionTier,
      });
      setEditing(null);
      await fetchSellers();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Star className="text-amber-500" size={28} />
          {t('title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600">{t('subtitle')}</p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
          {error}
          <button type="button" className="ml-3 underline" onClick={() => setError('')}>
            OK
          </button>
        </div>
      )}

      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          fetchSellers();
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('searchPlaceholder')}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white"
        >
          <Search size={16} />
          {t('search')}
        </button>
      </form>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-10 w-10 border-b-2 border-gray-800 rounded-full" />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('table.boutique')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('table.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('table.promotion')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('table.expires')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sellers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                    {t('messages.noSellers')}
                  </td>
                </tr>
              ) : (
                sellers.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.name}</div>
                      {row.ownerEmail && (
                        <div className="text-xs text-gray-500">{row.ownerEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.isPlatformStore || row.isPinned ? (
                        <span className="inline-flex rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5 text-xs font-semibold">
                          {t('badge.pinned')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.isPlatformStore ? (
                        <span className="text-gray-500">{t('messages.platformNoPromo')}</span>
                      ) : row.promotionActive ? (
                        <span className="inline-flex rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-xs font-semibold">
                          {t('badge.active')}
                        </span>
                      ) : (
                        <span className="text-gray-500">{t('badge.inactive')}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.promotionExpiresAt
                        ? new Date(row.promotionExpiresAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!row.isPlatformStore && (
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          <Pencil size={14} />
                          {t('editPromotion')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            {t('pagination.prev')}
          </button>
          <span className="text-sm text-gray-600 self-center">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            {t('pagination.next')}
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-1">{t('modal.title')}</h2>
            <p className="text-sm text-gray-600 mb-4">{editing.name}</p>
            <form onSubmit={handleSave} className="space-y-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.promotionActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, promotionActive: e.target.checked }))
                  }
                />
                {t('fields.promotionActive')}
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.promotionStartsAt')}
                </label>
                <input
                  type="datetime-local"
                  value={form.promotionStartsAt}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, promotionStartsAt: e.target.value }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.promotionExpiresAt')}
                </label>
                <input
                  type="datetime-local"
                  value={form.promotionExpiresAt}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, promotionExpiresAt: e.target.value }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fields.promotionTier')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.promotionTier}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      promotionTier: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 border rounded-md text-sm"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {saving ? '…' : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoutiquePromotions;
